import api from './api'
import { toFormData } from '../util/form-data'
import { EventType } from '../enum/provider/calendar/EventType'
import { EventFrequency } from '../enum/provider/calendar/EventFrequency'

/**
 * Agenda interna da Piperz (`provider_events`) — a mesma que o criativo vê em
 * /my-calendar no studio.
 *
 * Os campos abaixo são exatamente o que o InternalCalendarService devolve em
 * `getEventsForRange()`; nada é remontado aqui.
 */
export type CalendarEventColor = 'green' | 'red' | 'yellow' | 'blue' | 'slate'

export interface CalendarEvent {
  id: number
  /** Preenchido nas instâncias expandidas de um evento recorrente. */
  parent_id: number | null
  title: string
  /** Título já mascarado: evento vindo do Google aparece como "Compromisso pessoal". */
  display_title: string
  type: EventType
  type_text: string
  color: CalendarEventColor
  is_flexible: boolean
  /** Y-m-d */
  date: string
  /** HH:mm — nulo em evento flexível. */
  starts_at: string | null
  ends_at: string | null
  duration_minutes: number | null
  notes: string | null
  recurrence_type: EventFrequency
  recurrence_text: string
  /** Y-m-d — só existe em evento recorrente com data limite. */
  recurrence_until: string | null
  session_item_id: number | null
  session_id: number | null
  session_status: number | null
  session_status_text: string | null
  session_scheduled_for: string | null
  session_address: string | null
  session_reference: string | null
  client_id: number | null
  client_name: string | null
  item_title: string | null
  /** Duração do bloco do ensaio, em minutos. */
  session_duration_minutes: number | null
  /**
   * O que ESTE criativo pode ajustar neste ensaio. Vem do servidor já
   * resolvido (config da conta do cliente + permissão dele para este criativo)
   * e é reconferido na gravação — aqui serve só para decidir o que desenhar.
   */
  can_edit_session_duration: boolean
  can_edit_session_schedule: boolean
}

export interface CalendarStats {
  total: number
  ensaios: number
  bloqueios: number
  compromissos: number
}

export interface CalendarEventsResponse {
  data: CalendarEvent[]
  meta: { from: string; to: string; stats: CalendarStats }
}

/** Um intervalo livre (visão semanal) ou o mês inteiro (visões de mês e agenda). */
export type CalendarRange = { from: string; to: string } | { year: number; month: number }

/**
 * O evento como o formulário precisa dele — sempre relido do banco.
 *
 * A listagem devolve ocorrências: a instância de hoje de um evento semanal
 * carrega a data de hoje, não a do início da série. Editar a partir dela
 * arrastaria a série inteira.
 */
export interface EditableCalendarEvent {
  id: number
  title: string
  type: EventType
  all_day: boolean
  /** Y-m-d */
  date: string
  /** Y-m-d\THH:mm */
  starts_at: string
  ends_at: string
  recurrence_type: EventFrequency
  recurrence_until: string | null
  notes: string | null
}

export interface CalendarEventPayload {
  title: string
  type: EventType
  all_day: boolean
  /** Y-m-d — usado quando `all_day`. */
  date?: string
  /** Y-m-d\THH:mm — usado quando não é dia inteiro. */
  starts_at?: string
  ends_at?: string
  recurrence_type: EventFrequency
  recurrence_until?: string | null
  notes?: string | null
}

export async function fetchCalendarEvents(range: CalendarRange) {
  const { data } = await api.get<CalendarEventsResponse>('/provider/calendar/events', { params: range })
  return data
}

export async function fetchCalendarEvent(id: number) {
  const { data } = await api.get<{ data: EditableCalendarEvent }>(`/provider/calendar/events/${id}`)
  return data.data
}

/**
 * Cria ou edita um bloqueio/compromisso. A edição vai por POST porque o app
 * manda multipart, que o PHP não popula em requisições PUT.
 */
export async function saveCalendarEvent(payload: CalendarEventPayload, id?: number | null) {
  const url = id ? `/provider/calendar/events/${id}` : '/provider/calendar/events'
  const { data } = await api.post(url, toFormData(payload as any))
  return data
}

export async function deleteCalendarEvent(id: number) {
  const { data } = await api.delete(`/provider/calendar/events/${id}`)
  return data
}

// ── Ajuste do ensaio pelo criativo ──────────────────────────────────────────
//
// Duração e horário do ensaio, quando o cliente libera o criativo para mexer.
// A listagem já diz SE ele pode (can_edit_session_*); estes endpoints trazem os
// valores atuais do banco e gravam a mudança.

export interface SessionAdjustment {
  session_id: number
  /** Y-m-d\THH:mm */
  scheduled_for: string | null
  duration_minutes: number
  /** A duração já foi definida na mão, ou ainda vem do cálculo por serviços? */
  is_manual_duration: boolean
  can_edit_duration: boolean
  can_edit_schedule: boolean
}

export interface SessionAdjustmentPayload {
  duration_minutes?: number
  /** Y-m-d\THH:mm */
  scheduled_for?: string
}

export async function fetchSessionAdjustment(sessionId: number) {
  const { data } = await api.get<{ data: SessionAdjustment }>(
    `/provider/calendar/sessions/${sessionId}/adjustments`,
  )
  return data.data
}

export async function saveSessionAdjustment(sessionId: number, payload: SessionAdjustmentPayload) {
  const { data } = await api.post(
    `/provider/calendar/sessions/${sessionId}/adjustments`,
    toFormData(payload as any),
  )
  return data
}
